import { useState, useEffect, useRef } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, ReferenceLine, Cell
} from 'recharts';
import { api } from '../api';
import API_URL from '../api';
import CierreMensual from './CierreMensual';

const PERIODOS_MARGEN = [
  { label: 'Último mes',   value: 1  },
  { label: 'Últimos 3m',  value: 3  },
  { label: 'Últimos 6m',  value: 6  },
  { label: 'Últimos 12m', value: 12 },
];

const TooltipRevenue = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload;
  return (
    <div style={{ background: '#1a1a2e', border: '1px solid #2d2d44', borderRadius: 8, padding: '10px 14px', fontSize: 12 }}>
      <div style={{ color: '#fff', fontWeight: 'bold', marginBottom: 6 }}>{d?.label}</div>
      <div style={{ color: '#4cc9f0' }}>Revenue neto: <strong>${d?.revenue_neto?.toLocaleString('es-EC', { minimumFractionDigits: 2 })}</strong></div>
      <div style={{ color: '#aaa' }}>Revenue bruto: ${d?.revenue_bruto?.toLocaleString('es-EC', { minimumFractionDigits: 2 })}</div>
      {d?.devoluciones > 0 && (
        <div style={{ color: '#e63946' }}>Devoluciones: -${d?.devoluciones?.toFixed(2)} ({d?.total_devoluciones} NC)</div>
      )}
      <div style={{ color: '#aaa', marginTop: 4 }}>Facturas: {d?.total_facturas}</div>
      <div style={{ color: '#aaa' }}>Ticket prom.: ${d?.ticket_promedio?.toFixed(2)}</div>
      {d?.delta_pct !== null && d?.delta_pct !== undefined && (
        <div style={{ color: d.delta_pct >= 0 ? '#4caf50' : '#e63946', marginTop: 4 }}>
          {d.delta_pct >= 0 ? '▲' : '▼'} {Math.abs(d.delta_pct)}% vs mes anterior
        </div>
      )}
    </div>
  );
};

const MESES_ES = ['','Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'];
function periodoLabel(p) {
  if (!p) return '';
  const [a, m] = p.split('-');
  return `${MESES_ES[parseInt(m)]} ${a}`;
}

export default function Finanzas() {
  const [tab, setTab]                     = useState('revenue');
  const [revenue, setRevenue]             = useState(null);
  const [margen, setMargen]               = useState(null);
  const [cargandoRev, setCargandoRev]     = useState(true);
  const [cargandoMar, setCargandoMar]     = useState(true);
  const [periodoMargen, setPeriodoMargen] = useState(3);

  // TRF/Tarjeta
  const [trfMeses, setTrfMeses]           = useState(null);
  const [trfDetalle, setTrfDetalle]       = useState(null);
  const [trfMesActivo, setTrfMesActivo]   = useState(null);
  const [trfFiltro, setTrfFiltro]         = useState('todos');
  const [cargandoTrf, setCargandoTrf]     = useState(false);
  const [cargandoDetalle, setCargandoDetalle] = useState(false);

  // Datafast
  const [datafastMes, setDatafastMes]         = useState(null);
  const [datafastData, setDatafastData]       = useState(null);
  const [subiendo, setSubiendo]               = useState(false);
  const [dfExito, setDfExito]                 = useState('');
  const [dfError, setDfError]                 = useState('');
  const dfInputRef = useRef(null);
  const [drawerTarjeta, setDrawerTarjeta] = useState(null);
  const [modalManual, setModalManual]     = useState(null); // {id, factura, cliente, total}
  const [manualTipo, setManualTipo]       = useState('transferencia');
  const [manualObs, setManualObs]         = useState('');
  const [guardandoManual, setGuardandoManual] = useState(false);
  const [colapsadoDF, setColapsadoDF]     = useState(false); // resumen Datafast colapsado

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { cargarRevenue(); }, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { cargarMargen(); }, [periodoMargen]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (tab === 'trf' && !trfMeses) cargarTrf(); }, [tab]);

  const cargarTrf = async () => {
    setCargandoTrf(true);
    try {
      const data = await api.get('/finanzas/trf-tarjeta');
      setTrfMeses(data.error ? null : data.meses);
    } catch (e) { console.error(e); }
    finally { setCargandoTrf(false); }
  };

  const cargarDetalle = async (mes) => {
    setTrfMesActivo(mes);
    setTrfDetalle(null);
    setTrfFiltro('todos');
    setDatafastData(null); cargarDatafast(mes);
    setCargandoDetalle(true);
    try {
      const data = await api.get(`/finanzas/trf-tarjeta/${mes}`);
      setTrfDetalle(data.error ? null : data);
    } catch (e) { console.error(e); }
    finally { setCargandoDetalle(false); }
  };

  const cargarDatafast = async (mes) => {
    setDatafastMes(mes);
    setDatafastData(null);
    try {
      const data = await api.get(`/finanzas/datafast/${mes}`);
      setDatafastData(data.error ? null : data);
    } catch (e) { console.error(e); }
  };

  const subirDatafast = async (archivos) => {
    if (!archivos || archivos.length === 0) return;
    const archivo = archivos[0];
    if (!archivo.name.toLowerCase().endsWith('.xlsx')) {
      setDfError('Solo se aceptan archivos .xlsx'); return;
    }
    setSubiendo(true); setDfExito(''); setDfError('');
    try {
      const formData = new FormData();
      formData.append('archivo', archivo);
      const res  = await fetch(`${API_URL}/finanzas/datafast/upload`, { method: 'POST', body: formData });
      const data = await res.json();
      if (data.error) {
        setDfError(data.error);
      } else {
        setDfExito(`✅ ${data.total} transacciones procesadas · ${data.tarjetas_validadas} tarjetas validadas`);
        if (trfMesActivo) cargarDatafast(trfMesActivo);
        setTrfMeses(null); cargarTrf();
      }
    } catch (e) { setDfError('Error de conexión'); }
    finally { setSubiendo(false); if (dfInputRef.current) dfInputRef.current.value = ''; }
  };

  const guardarManual = async () => {
    if (!modalManual) return;
    setGuardandoManual(true);
    try {
      const data = await api.patch(`/finanzas/trf-tarjeta/${modalManual.id}/validar-manual`, {
        tipo_pago:   manualTipo,
        observacion: manualObs,
      });
      if (!data.error) {
        setModalManual(null);
        setTrfDetalle(null);
        cargarDetalle(trfMesActivo);
        setTrfMeses(null); cargarTrf();
      }
    } catch(e) { console.error(e); }
    finally { setGuardandoManual(false); }
  };

  const cargarRevenue = async () => {
    setCargandoRev(true);
    try {
      const data = await api.get('/finanzas/revenue');
      setRevenue(data.error ? null : data);
    } catch (e) { console.error(e); }
    finally { setCargandoRev(false); }
  };

  const cargarMargen = async () => {
    setCargandoMar(true);
    try {
      const data = await api.get(`/finanzas/margen?meses=${periodoMargen}`);
      setMargen(data.error ? null : data);
    } catch (e) { console.error(e); }
    finally { setCargandoMar(false); }
  };

  const colorBarra = (entry) => {
    if (entry.delta_pct === null || entry.delta_pct === undefined) return '#4cc9f0';
    return entry.delta_pct >= 0 ? '#4caf50' : '#e63946';
  };

  const colorMargen = (pct) => {
    if (pct >= 40) return '#4caf50';
    if (pct >= 20) return '#ffc107';
    if (pct >= 0)  return '#ff9800';
    return '#e63946';
  };

  const mesActual = revenue?.mes_actual;

  return (
    <>
      <style>{`
        @media print {
          body * { visibility: hidden; }
          #trf-print-area, #trf-print-area * { visibility: visible; }
          #trf-print-area { position: absolute; left: 0; top: 0; width: 100%; }
          #trf-scroll-area { max-height: none !important; overflow: visible !important; }
          @page { margin: 10mm; size: landscape; }
        }
      `}</style>
      {/* ── Header ── */}
      <div style={s.headerRow}>
        <div>
          <h2 style={s.titulo}>💰 Finanzas</h2>
          <p style={s.subtitulo}>Revenue real desde Morpheus · últimos 12 meses</p>
        </div>
        <button style={s.botonRefresh} onClick={() => {
          if (tab === 'revenue') { cargarRevenue(); cargarMargen(); }
          else { setTrfMeses(null); setTrfDetalle(null); cargarTrf(); }
        }}>🔄 Actualizar</button>
      </div>

      {/* ── Tabs ── */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 24 }}>
        {[
          { key: 'revenue', label: '📈 Revenue & Márgenes' },
          { key: 'trf',     label: '🔀 Transferencias & Tarjetas' },
          { key: 'cierre',  label: '💼 P&L Mensual' },   // ← agrega
        ].map(t => (
          <button key={t.key}
            style={{
              padding: '8px 20px', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 'bold',
              background: tab === t.key ? '#1a2a3e' : 'none',
              border: tab === t.key ? '1px solid #4cc9f0' : '1px solid #2d2d44',
              color: tab === t.key ? '#4cc9f0' : '#888',
            }}
            onClick={() => setTab(t.key)}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ══ TAB REVENUE ══ */}
      {tab !== 'revenue' ? null : <>

      {/* ══ SECCIÓN REVENUE ══ */}
      {cargandoRev ? (
        <p style={s.cargando}>Cargando datos financieros...</p>
      ) : !revenue ? (
        <p style={{ color: '#e63946' }}>No se pudo cargar revenue</p>
      ) : (
        <>
          {/* KPIs */}
          <div style={s.kpisRow}>
            <div style={{...s.kpiCard, borderTopColor: '#4cc9f0'}}>
              <div style={{...s.kpiValor, color: '#4cc9f0'}}>
                ${revenue.total_12m.toLocaleString('es-EC', { minimumFractionDigits: 2 })}
              </div>
              <div style={s.kpiLabel}>Revenue neto 12 meses</div>
            </div>
            <div style={{...s.kpiCard, borderTopColor: '#4caf50'}}>
              <div style={{...s.kpiValor, color: '#4caf50'}}>
                ${mesActual?.revenue_neto?.toLocaleString('es-EC', { minimumFractionDigits: 2 }) || '—'}
              </div>
              <div style={s.kpiLabel}>
                {mesActual?.label || 'Mes actual'}
                {mesActual?.delta_pct !== null && mesActual?.delta_pct !== undefined && (
                  <span style={{ marginLeft: 8, fontSize: 11, color: mesActual.delta_pct >= 0 ? '#4caf50' : '#e63946' }}>
                    {mesActual.delta_pct >= 0 ? '▲' : '▼'} {Math.abs(mesActual.delta_pct)}%
                  </span>
                )}
              </div>
            </div>
            <div style={{...s.kpiCard, borderTopColor: '#f72585'}}>
              <div style={{...s.kpiValor, color: '#f72585'}}>
                {mesActual?.total_facturas || 0}
              </div>
              <div style={s.kpiLabel}>Facturas mes actual</div>
            </div>
            <div style={{...s.kpiCard, borderTopColor: '#ffc107'}}>
              <div style={{...s.kpiValor, color: '#ffc107'}}>
                ${revenue.ticket_promedio_12m?.toFixed(2) || '—'}
              </div>
              <div style={s.kpiLabel}>Ticket promedio 12m</div>
            </div>
            <div style={{...s.kpiCard, borderTopColor: '#e63946'}}>
              <div style={{...s.kpiValor, color: '#e63946'}}>
                {revenue.total_devoluciones_12m || 0}
              </div>
              <div style={s.kpiLabel}>Notas crédito 12m</div>
            </div>
          </div>

          {/* Gráfico barras */}
          <div style={s.card}>
            <div style={s.cardHeader}>
              <span style={s.cardTitulo}>📈 Revenue mensual neto</span>
              <span style={s.cardSub}>
                <span style={{ color: '#4caf50' }}>■</span> Crecimiento &nbsp;
                <span style={{ color: '#e63946' }}>■</span> Caída &nbsp;
                <span style={{ color: '#4cc9f0' }}>■</span> Primer mes
              </span>
            </div>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={revenue.meses} margin={{ top: 8, right: 24, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#2a2a3e" vertical={false} />
                <XAxis dataKey="label_corto" stroke="#555" fontSize={11} tick={{ fill: '#888' }} />
                <YAxis stroke="#555" fontSize={11} tick={{ fill: '#888' }}
                  tickFormatter={v => `$${(v/1000).toFixed(0)}k`} />
                <Tooltip content={<TooltipRevenue />} />
                <ReferenceLine
                  y={revenue.total_12m / revenue.meses.length}
                  stroke="#444" strokeDasharray="4 4"
                  label={{ value: 'Prom.', fill: '#555', fontSize: 10, position: 'insideRight' }}
                />
                <Bar dataKey="revenue_neto" radius={[4, 4, 0, 0]}>
                  {revenue.meses.map((entry, i) => (
                    <Cell key={i} fill={colorBarra(entry)} fillOpacity={0.85} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Tabla mensual detallada */}
          <div style={s.card}>
            <div style={s.cardHeader}>
              <span style={s.cardTitulo}>📋 Detalle mensual</span>
            </div>
            <div style={s.tabla}>
              <div style={s.tablaHeader}>
                <span style={{ flex: 2 }}>MES</span>
                <span style={{ width: 80,  textAlign: 'center' }}>FACTURAS</span>
                <span style={{ width: 130, textAlign: 'right'  }}>REVENUE BRUTO</span>
                <span style={{ width: 120, textAlign: 'right'  }}>DEVOLUCIONES</span>
                <span style={{ width: 130, textAlign: 'right'  }}>REVENUE NETO</span>
                <span style={{ width: 110, textAlign: 'right'  }}>TICKET PROM.</span>
                <span style={{ width: 90,  textAlign: 'center' }}>Δ MES ANT.</span>
              </div>

              {[...revenue.meses].reverse().map((m, i) => (
                <div key={i} style={{
                  ...s.tablaRow,
                  background: i % 2 === 0 ? '#1e1e2e' : '#1a1a2e',
                  ...(i === 0 ? { borderLeft: '3px solid #4caf50' } : {})
                }}>
                  <span style={{ flex: 2, color: i === 0 ? '#fff' : '#aaa', fontWeight: i === 0 ? 'bold' : 'normal' }}>
                    {m.label}
                    {i === 0 && <span style={{ color: '#4caf50', fontSize: 10, marginLeft: 8 }}>● Actual</span>}
                  </span>
                  <span style={{ width: 80, textAlign: 'center', color: '#aaa' }}>{m.total_facturas}</span>
                  <span style={{ width: 130, textAlign: 'right', color: '#555', fontSize: 12 }}>
                    ${m.revenue_bruto.toLocaleString('es-EC', { minimumFractionDigits: 2 })}
                  </span>
                  <span style={{ width: 120, textAlign: 'right', fontSize: 12 }}>
                    {m.devoluciones > 0 ? (
                      <span style={{ color: '#e63946' }}>
                        -${m.devoluciones.toFixed(2)}
                        <span style={{ color: '#555', fontSize: 10, marginLeft: 4 }}>
                          ({m.total_devoluciones} NC)
                        </span>
                      </span>
                    ) : (
                      <span style={{ color: '#333' }}>—</span>
                    )}
                  </span>
                  <span style={{ width: 130, textAlign: 'right', color: '#4cc9f0', fontWeight: 'bold' }}>
                    ${m.revenue_neto.toLocaleString('es-EC', { minimumFractionDigits: 2 })}
                  </span>
                  <span style={{ width: 110, textAlign: 'right', color: '#aaa', fontSize: 12 }}>
                    ${m.ticket_promedio.toFixed(2)}
                  </span>
                  <span style={{ width: 90, textAlign: 'center' }}>
                    {m.delta_pct !== null && m.delta_pct !== undefined ? (
                      <span style={{
                        background: m.delta_pct >= 0 ? '#1a3a2a' : '#3a1a1a',
                        color: m.delta_pct >= 0 ? '#4caf50' : '#e63946',
                        borderRadius: 6, padding: '2px 8px', fontSize: 11, fontWeight: 'bold'
                      }}>
                        {m.delta_pct >= 0 ? '▲' : '▼'} {Math.abs(m.delta_pct)}%
                      </span>
                    ) : '—'}
                  </span>
                </div>
              ))}

              {/* Fila de totales */}
              <div style={{ ...s.tablaRow, background: '#0f0f1a', borderTop: '2px solid #2d2d44' }}>
                <span style={{ flex: 2, color: '#fff', fontWeight: 'bold' }}>TOTAL 12 MESES</span>
                <span style={{ width: 80, textAlign: 'center', color: '#fff', fontWeight: 'bold' }}>
                  {revenue.total_facturas_12m}
                </span>
                <span style={{ width: 130, textAlign: 'right', color: '#555', fontSize: 12 }}>
                  ${revenue.meses.reduce((a, m) => a + m.revenue_bruto, 0)
                    .toLocaleString('es-EC', { minimumFractionDigits: 2 })}
                </span>
                <span style={{ width: 120, textAlign: 'right', color: '#e63946', fontSize: 12 }}>
                  {revenue.meses.reduce((a, m) => a + m.devoluciones, 0) > 0
                    ? `-$${revenue.meses.reduce((a, m) => a + m.devoluciones, 0).toFixed(2)}`
                    : '—'}
                </span>
                <span style={{ width: 130, textAlign: 'right', color: '#ffc107', fontWeight: 'bold', fontSize: 15 }}>
                  ${revenue.total_12m.toLocaleString('es-EC', { minimumFractionDigits: 2 })}
                </span>
                <span style={{ width: 110, textAlign: 'right', color: '#aaa', fontSize: 12 }}>
                  ${revenue.ticket_promedio_12m?.toFixed(2)}
                </span>
                <span style={{ width: 90 }} />
              </div>
            </div>
          </div>
        </>
      )}

      {/* ══ SECCIÓN MARGEN ══ */}
      <div style={{ ...s.headerRow, marginTop: 16 }}>
        <div>
          <h2 style={{ ...s.titulo, fontSize: 18 }}>📊 Margen por Marca</h2>
          <p style={s.subtitulo}>Revenue real de facturas vs último costo de compra por ítem</p>
        </div>
        <div style={s.periodoRow}>
          {PERIODOS_MARGEN.map(p => (
            <button key={p.value}
              style={{ ...s.periodoBtn, ...(periodoMargen === p.value ? s.periodoBtnActivo : {}) }}
              onClick={() => setPeriodoMargen(p.value)}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {cargandoMar ? (
        <p style={s.cargando}>Calculando márgenes...</p>
      ) : !margen ? (
        <p style={{ color: '#e63946' }}>No se pudo cargar margen</p>
      ) : (
        <>
          {/* KPIs margen */}
          <div style={s.kpisRow}>
            <div style={{...s.kpiCard, borderTopColor: '#4caf50'}}>
              <div style={{...s.kpiValor, color: '#4caf50'}}>{margen.margen_global}%</div>
              <div style={s.kpiLabel}>Margen promedio global</div>
            </div>
            <div style={{...s.kpiCard, borderTopColor: '#4cc9f0'}}>
              <div style={{...s.kpiValor, color: '#4cc9f0'}}>
                ${margen.revenue_total.toLocaleString('es-EC', { minimumFractionDigits: 2 })}
              </div>
              <div style={s.kpiLabel}>Revenue período</div>
            </div>
            <div style={{...s.kpiCard, borderTopColor: '#f72585'}}>
              <div style={{...s.kpiValor, color: '#f72585'}}>
                ${margen.costo_total?.toLocaleString('es-EC', { minimumFractionDigits: 2 }) || '—'}
              </div>
              <div style={s.kpiLabel}>Costo total período</div>
            </div>
            <div style={{...s.kpiCard, borderTopColor: '#7209b7'}}>
              <div style={{...s.kpiValor, color: '#7209b7'}}>{margen.total_marcas}</div>
              <div style={s.kpiLabel}>Marcas activas</div>
            </div>
          </div>

          {/* Ranking marcas */}
          <div style={s.card}>
            <div style={s.cardHeader}>
              <span style={s.cardTitulo}>🏆 Ranking de marcas por margen</span>
              <span style={s.cardSub}>
                <span style={{ color: '#4caf50' }}>●</span> ≥40% excelente &nbsp;
                <span style={{ color: '#ffc107' }}>●</span> 20–39% bueno &nbsp;
                <span style={{ color: '#ff9800' }}>●</span> 0–19% bajo &nbsp;
                <span style={{ color: '#e63946' }}>●</span> negativo
              </span>
            </div>
            <div style={s.tabla}>
              <div style={s.tablaHeader}>
                <span style={{ width: 36, textAlign: 'center' }}>#</span>
                <span style={{ flex: 3 }}>MARCA</span>
                <span style={{ width: 90,  textAlign: 'center' }}>UNIDADES</span>
                <span style={{ width: 120, textAlign: 'right'  }}>REVENUE</span>
                <span style={{ width: 120, textAlign: 'right'  }}>COSTO EST.</span>
                <span style={{ width: 110, textAlign: 'right'  }}>MARGEN $</span>
                <span style={{ width: 160, textAlign: 'right'  }}>MARGEN %</span>
              </div>

              {margen.marcas.map((m, i) => {
                const color  = colorMargen(m.margen_pct);
                const maxPct = Math.max(...margen.marcas.map(x => x.margen_pct || 0)) || 100;
                return (
                  <div key={i} style={{
                    ...s.tablaRow,
                    background: i % 2 === 0 ? '#1e1e2e' : '#1a1a2e'
                  }}>
                    <span style={{ width: 36, textAlign: 'center', color: '#555', fontSize: 11 }}>
                      {i + 1}
                    </span>
                    <span style={{ flex: 3 }}>
                      <div style={{ color: '#fff', fontSize: 13, fontWeight: '600' }}>{m.marca}</div>
                      {m.sin_costo > 0 && (
                        <div style={{ color: '#555', fontSize: 10 }}>
                          {m.cobertura_pct < 100 ? `costo en ${m.cobertura_pct}% de ítems` : ''}
                          {!m.tiene_costo ? 'sin costo en inventario' : ''}
                        </div>
                      )}
                    </span>
                    <span style={{ width: 90, textAlign: 'center', color: '#aaa', fontSize: 12 }}>
                      {m.unidades.toFixed(0)}
                    </span>
                    <span style={{ width: 120, textAlign: 'right', color: '#4cc9f0', fontSize: 13 }}>
                      ${m.revenue.toLocaleString('es-EC', { minimumFractionDigits: 2 })}
                    </span>
                    <span style={{ width: 120, textAlign: 'right', color: '#aaa', fontSize: 12 }}>
                      {m.tiene_costo
                        ? `$${m.costo_total.toLocaleString('es-EC', { minimumFractionDigits: 2 })}`
                        : '—'}
                    </span>
                    <span style={{ width: 110, textAlign: 'right', color, fontWeight: 'bold', fontSize: 13 }}>
                      {m.tiene_costo ? `$${m.margen_valor.toFixed(2)}` : '—'}
                    </span>
                    <span style={{ width: 160, textAlign: 'right' }}>
                      {m.tiene_costo ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'flex-end' }}>
                          <div style={{ width: 70, background: '#2a2a3e', borderRadius: 3, height: 6 }}>
                            <div style={{
                              width: `${Math.min(Math.max((m.margen_pct / maxPct) * 100, 0), 100)}%`,
                              height: 6, borderRadius: 3, background: color
                            }} />
                          </div>
                          <span style={{
                            background: color + '22', color,
                            borderRadius: 6, padding: '2px 8px',
                            fontSize: 12, fontWeight: 'bold',
                            minWidth: 52, textAlign: 'center'
                          }}>
                            {m.margen_pct}%
                          </span>
                        </div>
                      ) : (
                        <span style={{ color: '#333', fontSize: 11 }}>sin datos</span>
                      )}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

        </>
      )}
      </> }

      {/* ══ TAB TRF/TARJETA ══ */}
      {tab === 'trf' && (
        <div>
          {cargandoTrf ? (
            <p style={s.cargando}>Cargando datos...</p>
          ) : !trfMeses ? (
            <p style={{ color: '#e63946' }}>No se pudieron cargar los datos</p>
          ) : (
            <>
              {/* Tarjetas resumen por mes */}
              <div style={{ display: 'flex', gap: 16, marginBottom: 24, flexWrap: 'wrap' }}>
                {trfMeses.map(m => {
                  const pendTotal  = (m.pendientes_banco || 0) + (m.pendientes_datafast || 0);
                  const validTotal = (m.validadas_banco  || 0) + (m.validadas_datafast  || 0);
                  const totalConc  = pendTotal + validTotal;
                  const pct        = totalConc > 0 ? Math.round(validTotal / totalConc * 100) : 0;
                  const colorPct   = pct === 100 ? '#4caf50' : pct >= 50 ? '#ffc107' : '#e63946';
                  const activo     = trfMesActivo === m.mes;
                  return (
                    <div key={m.mes}
                      onClick={() => cargarDetalle(m.mes)}
                      style={{
                        background: activo ? '#1a2a3e' : '#1e1e2e',
                        border: `1px solid ${activo ? '#4cc9f0' : '#2d2d44'}`,
                        borderRadius: 12, padding: 20, flex: 1, minWidth: 220,
                        cursor: 'pointer', transition: 'all 0.2s',
                      }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                        <span style={{ color: '#fff', fontWeight: 'bold', fontSize: 16 }}>{m.label}</span>
                        <span style={{ background: '#0f0f1a', color: colorPct, borderRadius: 6, padding: '2px 10px', fontSize: 11, fontWeight: 'bold' }}>
                          {pct}% conciliado
                        </span>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 16px' }}>
                        {[
                          ['TOTAL COBRADO',   `$${(m.total_cobrado||0).toLocaleString('es-EC', {minimumFractionDigits:2})}`,  '#fff'],
                          ['⏳ PENDIENTES',   pendTotal,                                                                        '#ffc107'],
                          ['🔀 DEPÓSITO/TRF', `$${(m.deposito_trf||0).toLocaleString('es-EC', {minimumFractionDigits:2})}`,  '#4cc9f0'],
                          ['💳 TARJETA',      `$${(m.tarjeta||0).toLocaleString('es-EC', {minimumFractionDigits:2})}`,        '#f72585'],
                          ['# Depósitos',     m.fact_deposito || 0,                                                           '#4cc9f0'],
                          ['# Tarjetas',      m.fact_tarjeta  || 0,                                                           '#f72585'],
                        ].map(([label, val, color]) => (
                          <div key={label}>
                            <div style={{ color: '#555', fontSize: 10, fontWeight: 'bold', letterSpacing: 1 }}>{label}</div>
                            <div style={{ color, fontSize: 13, fontWeight: 'bold', marginTop: 2 }}>{val}</div>
                          </div>
                        ))}
                      </div>
                      {/* Barra progreso conciliación */}
                      <div style={{ marginTop: 12, height: 4, background: '#2d2d44', borderRadius: 2, overflow: 'hidden' }}>
                        <div style={{ width: `${pct}%`, height: '100%', background: colorPct, borderRadius: 2, transition: 'width 0.5s' }} />
                      </div>
                    </div>
                  );
                })}
              </div>


              {/* ══ SECCIÓN DATAFAST ══ */}
              {trfMesActivo && (
                <div style={{ ...s.card, marginTop: 0 }}>
                  <div style={s.cardHeader}>
                    <span style={s.cardTitulo}>💳 Datafast — {periodoLabel(trfMesActivo)}</span>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      {dfExito && <span style={{ color: '#4caf50', fontSize: 12 }}>{dfExito}</span>}
                      {dfError && <span style={{ color: '#e63946', fontSize: 12 }}>{dfError}</span>}
                      <input ref={dfInputRef} type="file" accept=".xlsx" style={{ display: 'none' }}
                        onChange={e => e.target.files.length > 0 && subirDatafast(e.target.files)} />
                      <button style={{ background: subiendo ? '#2d2d44' : '#1a2a3e', border: '1px solid #4cc9f0', borderRadius: 8, color: '#4cc9f0', padding: '6px 16px', cursor: 'pointer', fontSize: 12, fontWeight: 'bold' }}
                        onClick={() => dfInputRef.current?.click()} disabled={subiendo}>
                        {subiendo ? '⏳ Procesando...' : '📤 Subir Excel Datafast'}
                      </button>
                    </div>
                  </div>

                  {datafastData && (
                    <>
                      {/* Resumen colapsable por adquirente */}
                      <div
                        onClick={() => setColapsadoDF(!colapsadoDF)}
                        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', padding: '6px 0', marginBottom: colapsadoDF ? 0 : 12 }}>
                        <span style={{ color: '#555', fontSize: 11, letterSpacing: 1 }}>
                          RESUMEN DATAFAST — {datafastData.total_trx} trx · ${datafastData.total_valor.toLocaleString('es-EC', {minimumFractionDigits:2})} · {datafastData.total_validadas} validadas
                        </span>
                        <span style={{ color: '#555', fontSize: 12 }}>{colapsadoDF ? '▶' : '▼'}</span>
                      </div>
                      {!colapsadoDF && (
                        <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
                          {datafastData.resumen.map(r => {
                            const color = r.adquirente === 'BANCO GUAYAQUIL' ? '#ffc107' :
                                          r.adquirente === 'PACIFICARD' ? '#4cc9f0' : '#f72585';
                            return (
                              <div key={r.adquirente} style={{ background: '#0f0f1a', borderRadius: 6, padding: '8px 14px', display: 'flex', gap: 16, alignItems: 'center', border: `1px solid ${color}33` }}>
                                <span style={{ color, fontSize: 11, fontWeight: 'bold' }}>{r.adquirente}</span>
                                <span style={{ color: '#aaa', fontSize: 11 }}>{r.trx} trx</span>
                                <span style={{ color: '#fff', fontSize: 12, fontWeight: 'bold' }}>${r.total.toLocaleString('es-EC', {minimumFractionDigits:2})}</span>
                                <span style={{ color: '#4caf50', fontSize: 11 }}>✅ {r.validadas}</span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* Detalle facturas del mes seleccionado */}
              {trfMesActivo && (
                <div style={s.card}>
                  <div style={s.cardHeader}>
                    <span style={s.cardTitulo}>
                      📋 {periodoLabel(trfMesActivo)} — Facturas TRF/Tarjeta
                    </span>
                    <div style={{ display: 'flex', gap: 6 }}>
                      {[
                        { key: 'todos',      label: `Todos (${trfDetalle?.count || 0})` },
                        { key: 'deposito',   label: `🔀 Depósito/Trf.` },
                        { key: 'tarjeta',    label: `💳 Tarjeta` },
                        { key: 'cheque',     label: `🏦 Cheque` },
                        { key: 'pendiente',  label: `⏳ Pendientes (${trfDetalle?.pendientes || 0})` },
                      ].map(f => (
                        <button key={f.key}
                          style={{
                            ...s.periodoBtn,
                            ...(trfFiltro === f.key ? s.periodoBtnActivo : {}),
                          }}
                          onClick={() => setTrfFiltro(f.key)}>
                          {f.label}
                        </button>
                      ))}
                      <button
                        onClick={() => window.print()}
                        style={{ ...s.periodoBtn, marginLeft: 8, borderColor: '#4cc9f0', color: '#4cc9f0' }}>
                        🖨️ Imprimir
                      </button>
                    </div>
                  </div>

                  {cargandoDetalle ? (
                    <p style={s.cargando}>Cargando facturas...</p>
                  ) : !trfDetalle ? (
                    <p style={{ color: '#e63946', padding: 20 }}>No se pudo cargar el detalle</p>
                  ) : (
                    <>
                      {/* KPIs detalle */}
                      <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
                        {[
                          ['🔀 Depósito/Trf.', `$${(trfDetalle.total_deposito||0).toLocaleString('es-EC', {minimumFractionDigits:2})}`, '#4cc9f0'],
                          ['💳 Tarjeta',       `$${(trfDetalle.total_tarjeta||0).toLocaleString('es-EC', {minimumFractionDigits:2})}`,  '#f72585'],
                          ['🏦 Cheque',        `$${(trfDetalle.total_cheque||0).toLocaleString('es-EC', {minimumFractionDigits:2})}`,   '#4361ee'],
                          ['Total cobrado',    `$${(trfDetalle.total_cobrado||0).toLocaleString('es-EC', {minimumFractionDigits:2})}`,  '#fff'],
                          ['⏳ Pendientes',    trfDetalle.pendientes || 0,                                                              '#ffc107'],
                        ].map(([label, val, color]) => (
                          <div key={label} style={{ background: '#0f0f1a', borderRadius: 8, padding: '10px 16px', flex: 1 }}>
                            <div style={{ color: '#555', fontSize: 10, fontWeight: 'bold', letterSpacing: 1 }}>{label}</div>
                            <div style={{ color, fontSize: 16, fontWeight: 'bold', marginTop: 4 }}>{val}</div>
                          </div>
                        ))}
                      </div>

                      {/* Tabla facturas */}
                      <div id="trf-print-area" style={s.tabla}>
                        <div style={s.tablaHeader}>
                          <span style={{ width: 95 }}>FECHA</span>
                          <span style={{ width: 150 }}>FACTURA</span>
                          <span style={{ flex: 3 }}>CLIENTE</span>
                          <span style={{ width: 110, textAlign: 'right' }}>TOTAL</span>
                          <span style={{ width: 180, textAlign: 'center' }}>ESTADO</span>
                        </div>
                        <div id="trf-scroll-area" style={{ maxHeight: 420, overflowY: 'auto' }}>
                          {trfDetalle.facturas.filter(f => {
                            if (trfFiltro === 'deposito')  return f.for_pag === 7 || f.for_pag === 11;
                            if (trfFiltro === 'tarjeta')   return f.for_pag === 5 || f.for_pag === 15;
                            if (trfFiltro === 'cheque')    return f.for_pag === 2;
                            if (trfFiltro === 'pendiente') return !f.validado;
                            return true;
                          }).map((f, i) => {
                            const colorFP = f.for_pag === 2              ? '#4361ee' :
                                            f.for_pag === 5 || f.for_pag === 15 ? '#f72585' : '#4cc9f0';
                            return (
                            <div key={`${f.factura}-${i}`} style={{
                              ...s.tablaRow,
                              background: i%2===0 ? '#1e1e2e' : '#1a1a2e',
                              borderLeft: `3px solid ${f.validado ? colorFP : '#ffc107'}`,
                            }}>
                              <span style={{ width: 95, color: '#aaa', fontSize: 11 }}>{f.fecha}</span>
                              <span style={{ width: 150, color: '#4cc9f0', fontSize: 11, fontFamily: 'monospace' }}>{f.factura}</span>
                              <span style={{ flex: 3 }}>
                                <div style={{ color: '#fff', fontSize: 12 }}>{f.cliente}</div>
                                <div style={{ color: '#555', fontSize: 10 }}>{f.cedula}</div>
                              </span>
                              <span style={{ width: 110, textAlign: 'right', color: '#fff', fontWeight: 'bold', fontSize: 13 }}>
                                ${(f.valor_pago||0).toLocaleString('es-EC', {minimumFractionDigits:2})}
                              </span>
                              <span style={{ width: 180, textAlign: 'center' }}>
                                {f.validado ? (
                                  f.for_pag === 5 || f.for_pag === 15 ? (
                                    <span
                                      onClick={() => {
                                        const df = datafastData?.detalle?.find(d => d.factura === f.factura);
                                        if (df) setDrawerTarjeta({ ...df, factura_num: f.factura, cliente: f.cliente });
                                      }}
                                      style={{
                                        background: '#2a1a3a', color: '#f72585',
                                        borderRadius: 6, padding: '3px 10px', fontSize: 10, fontWeight: 'bold',
                                        cursor: datafastData?.detalle?.find(d => d.factura === f.factura) ? 'pointer' : 'default',
                                        border: '1px solid #f7258533',
                                      }}>
                                      💳 {f.forma_pago} ›
                                    </span>
                                  ) : (
                                    <span style={{ background: '#1a3a2a', color: '#4caf50', borderRadius: 6, padding: '3px 10px', fontSize: 10, fontWeight: 'bold' }}>
                                      ✅ {f.forma_pago}
                                    </span>
                                  )
                                ) : (
                                  <span
                                    onClick={() => {
                                      if (f.pg_id) {
                                        setModalManual({id: f.pg_id, factura: f.factura, cliente: f.cliente, total: f.valor_pago});
                                        setManualTipo('transferencia');
                                        setManualObs('');
                                      }
                                    }}
                                    style={{
                                      background: '#2a2a1a', color: '#ffc107',
                                      borderRadius: 6, padding: '3px 10px', fontSize: 10, fontWeight: 'bold',
                                      cursor: f.pg_id ? 'pointer' : 'default',
                                      border: '1px solid #ffc10733',
                                    }}>
                                    ⏳ Pendiente {f.pg_id ? '›' : ''}
                                  </span>
                                )}
                              </span>
                            </div>
                          );})}
                        </div>
                        {/* Footer total */}
                        <div style={{ display: 'flex', padding: '10px 16px', background: '#0f0f1a', borderTop: '2px solid #2d2d44', gap: 8 }}>
                          <span style={{ width: 95 }} />
                          <span style={{ width: 150 }} />
                          <span style={{ flex: 3, color: '#aaa', fontSize: 12 }}>{trfDetalle.count} facturas</span>
                          <span style={{ width: 110, textAlign: 'right', color: '#fff', fontWeight: 'bold' }}>
                            ${(trfDetalle.total_cobrado||0).toLocaleString('es-EC', {minimumFractionDigits:2})}
                          </span>
                          <span style={{ width: 180 }} />
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ══ TAB P&L MENSUAL ══ */}
      {tab === 'cierre' && <CierreMensual />}

      {/* ══ MODAL VALIDACIÓN MANUAL ══ */}
      {modalManual && (
        <>
          <div onClick={() => setModalManual(null)}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 200 }} />
          <div style={{
            position: 'fixed', top: '50%', left: '50%',
            transform: 'translate(-50%, -50%)',
            background: '#1e1e2e', borderRadius: 12, padding: 28,
            width: 420, zIndex: 201, border: '1px solid #2d2d44',
            boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
          }}>
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <div style={{ color: '#ffc107', fontWeight: 'bold', fontSize: 15 }}>✏️ Validación Manual</div>
              <button onClick={() => setModalManual(null)}
                style={{ background: 'none', border: 'none', color: '#555', fontSize: 18, cursor: 'pointer' }}>✕</button>
            </div>

            {/* Info factura */}
            <div style={{ background: '#0f0f1a', borderRadius: 8, padding: '12px 16px', marginBottom: 20 }}>
              <div style={{ color: '#4cc9f0', fontSize: 12, fontFamily: 'monospace', marginBottom: 4 }}>{modalManual.factura}</div>
              <div style={{ color: '#fff', fontSize: 13, marginBottom: 2 }}>{modalManual.cliente}</div>
              <div style={{ color: '#4caf50', fontWeight: 'bold', fontSize: 16 }}>
                ${modalManual.total.toLocaleString('es-EC', {minimumFractionDigits:2})}
              </div>
            </div>

            {/* Tipo pago */}
            <div style={{ marginBottom: 16 }}>
              <div style={{ color: '#555', fontSize: 11, fontWeight: 'bold', letterSpacing: 1, marginBottom: 8 }}>TIPO DE PAGO</div>
              <div style={{ display: 'flex', gap: 8 }}>
                {[
                  { key: 'transferencia', label: '🔀 Transferencia', color: '#4cc9f0' },
                  { key: 'tarjeta',       label: '💳 Tarjeta',       color: '#f72585' },
                ].map(t => (
                  <button key={t.key}
                    onClick={() => setManualTipo(t.key)}
                    style={{
                      flex: 1, padding: '10px 0', borderRadius: 8, cursor: 'pointer', fontWeight: 'bold', fontSize: 13,
                      background: manualTipo === t.key ? '#1a2a3e' : '#0f0f1a',
                      border: `1px solid ${manualTipo === t.key ? t.color : '#2d2d44'}`,
                      color: manualTipo === t.key ? t.color : '#555',
                    }}>
                    {t.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Observación */}
            <div style={{ marginBottom: 24 }}>
              <div style={{ color: '#555', fontSize: 11, fontWeight: 'bold', letterSpacing: 1, marginBottom: 8 }}>OBSERVACIÓN</div>
              <textarea
                value={manualObs}
                onChange={e => setManualObs(e.target.value)}
                placeholder="Ej: 2 transferencias $10 + $7 mismo cliente..."
                style={{
                  width: '100%', background: '#0f0f1a', border: '1px solid #2d2d44',
                  borderRadius: 8, color: '#fff', padding: '10px 14px',
                  fontSize: 13, resize: 'vertical', minHeight: 72,
                  boxSizing: 'border-box', fontFamily: 'inherit',
                }}
              />
            </div>

            {/* Botones */}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={() => setModalManual(null)}
                style={{ background: 'none', border: '1px solid #2d2d44', borderRadius: 8, color: '#aaa', padding: '10px 20px', cursor: 'pointer', fontSize: 13 }}>
                Cancelar
              </button>
              <button onClick={guardarManual} disabled={guardandoManual}
                style={{ background: '#1a3a2a', border: '1px solid #4caf50', borderRadius: 8, color: '#4caf50', padding: '10px 24px', cursor: 'pointer', fontWeight: 'bold', fontSize: 13, opacity: guardandoManual ? 0.6 : 1 }}>
                {guardandoManual ? 'Guardando...' : '✅ Validar'}
              </button>
            </div>
          </div>
        </>
      )}

      {/* ══ DRAWER TARJETA ══ */}
      {drawerTarjeta && (
        <>
          {/* Overlay */}
          <div onClick={() => setDrawerTarjeta(null)}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 100 }} />
          {/* Panel */}
          <div style={{
            position: 'fixed', top: 0, right: 0, bottom: 0, width: 360,
            background: '#1e1e2e', zIndex: 101, display: 'flex', flexDirection: 'column',
            boxShadow: '-4px 0 24px rgba(0,0,0,0.5)',
            animation: 'slideInRight 0.2s ease',
          }}>
            {/* Header */}
            <div style={{ padding: '20px 24px', borderBottom: '1px solid #2d2d44', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <div style={{ color: '#f72585', fontWeight: 'bold', fontSize: 16, marginBottom: 4 }}>💳 Pago con Tarjeta</div>
                <div style={{ color: '#aaa', fontSize: 12 }}>{drawerTarjeta.factura_num}</div>
                {drawerTarjeta.cliente && <div style={{ color: '#fff', fontSize: 13, marginTop: 4 }}>{drawerTarjeta.cliente}</div>}
              </div>
              <button onClick={() => setDrawerTarjeta(null)}
                style={{ background: 'none', border: 'none', color: '#555', fontSize: 20, cursor: 'pointer', padding: 4 }}>✕</button>
            </div>

            {/* Contenido */}
            <div style={{ flex: 1, overflowY: 'auto', padding: 24 }}>
              {/* Adquirente badge */}
              <div style={{ marginBottom: 24, textAlign: 'center' }}>
                {(() => {
                  const color = drawerTarjeta.adquirente === 'BANCO GUAYAQUIL' ? '#ffc107' :
                                drawerTarjeta.adquirente === 'PACIFICARD' ? '#4cc9f0' : '#f72585';
                  return (
                    <span style={{ background: '#0f0f1a', color, border: `1px solid ${color}`, borderRadius: 8, padding: '6px 20px', fontSize: 13, fontWeight: 'bold' }}>
                      {drawerTarjeta.adquirente}
                    </span>
                  );
                })()}
              </div>

              {/* Datos del pago */}
              {[
                ['VALOR COBRADO',   `$${drawerTarjeta.valor.toLocaleString('es-EC', {minimumFractionDigits:2})}`, '#fff', 22],
                ['TIPO CRÉDITO',    drawerTarjeta.tipo_credito, '#4cc9f0', 14],
                ['FECHA TRANSACCIÓN', drawerTarjeta.fecha_trx, '#aaa', 14],
                ['FECHA CAPTURA',   drawerTarjeta.fecha_captura, '#aaa', 14],
                ['LOTE',            drawerTarjeta.lote, '#aaa', 14],
                ['TDTLF',           String(drawerTarjeta.tdtlf), '#555', 12],
              ].map(([label, val, color, size]) => (
                <div key={label} style={{ marginBottom: 16, padding: '12px 16px', background: '#0f0f1a', borderRadius: 8 }}>
                  <div style={{ color: '#555', fontSize: 10, fontWeight: 'bold', letterSpacing: 1, marginBottom: 4 }}>{label}</div>
                  <div style={{ color, fontSize: size, fontWeight: size >= 18 ? 'bold' : 'normal' }}>{val}</div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </>
  );
}

const s = {
  headerRow:        { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 },
  titulo:           { color: '#fff', marginBottom: 4 },
  subtitulo:        { color: '#555', fontSize: 13 },
  botonRefresh:     { background: 'none', border: '1px solid #444', borderRadius: 8, color: '#aaa', padding: '8px 16px', cursor: 'pointer', fontSize: 13 },
  cargando:         { color: '#aaa', padding: 40, textAlign: 'center' },
  kpisRow:          { display: 'flex', gap: 16, marginBottom: 24, flexWrap: 'wrap' },
  kpiCard:          { background: '#1e1e2e', borderRadius: 12, padding: 20, flex: 1, minWidth: 140, borderTop: '3px solid #7209b7' },
  kpiValor:         { fontSize: 26, fontWeight: 'bold', color: '#fff', marginBottom: 6 },
  kpiLabel:         { color: '#aaa', fontSize: 12 },
  card:             { background: '#1e1e2e', borderRadius: 12, padding: 24, marginBottom: 24 },
  cardHeader:       { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 8 },
  cardTitulo:       { color: '#fff', fontWeight: 'bold', fontSize: 15 },
  cardSub:          { color: '#555', fontSize: 11 },
  tabla:            { borderRadius: 8, overflow: 'hidden' },
  tablaHeader:      { display: 'flex', padding: '10px 16px', background: '#0f0f1a', color: '#555', fontSize: 11, fontWeight: 'bold', gap: 8 },
  tablaRow:         { display: 'flex', padding: '12px 16px', gap: 8, alignItems: 'center' },
  periodoRow:       { display: 'flex', gap: 4 },
  periodoBtn:       { background: '#1e1e2e', border: '1px solid #2d2d44', borderRadius: 6, color: '#888', padding: '6px 12px', cursor: 'pointer', fontSize: 12 },
  periodoBtnActivo: { background: '#2d2d44', border: '1px solid #4cc9f0', color: '#4cc9f0', fontWeight: 'bold' },
};